import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Button from "@pathwright/ui/src/components/button/Button"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import get from "lodash/get"
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react"
import { useGroupQuery } from "../../api/generated"
import { userCanInvite } from "../../group/permissions"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import {
  getCommunityAreaUrl,
  getCommunityAreaUrlValues
} from "../../utils/urls"
import { useCommunityContext } from "../Community"
import {
  COMMUNITY_AREA_ACTIVITY,
  COMMUNITY_AREA_DISCUSSION,
  COMMUNITY_AREA_MEMBERS,
  COMMUNITY_FEATURE_VISIBILITY_MAP
} from "../constants"
import CommunityActivity from "./activity/CommunityActivity"
import CommunityDiscussion from "./discussion/CommunityDiscussion"
import CommunityMembers from "./members/CommunityMembers"
import useCommunityAreaFeaturePermission from "./useCommunityAreaFeaturePermission"

const CommunityAreaContext = React.createContext()

const RenderInviteControl = ({ t, cohort }) => {
  const pwContext = usePathwrightContext()
  const { resourceSlug, cohortId } = getCommunityAreaUrlValues()
  const inviteUrl = getCommunityAreaUrl(resourceSlug, cohortId, "invite")

  return userCanInvite(pwContext, cohort) ? (
    <Button
      styleType="primary"
      icon="groupadd-fill"
      to={inviteUrl}
      label={t("Invite")}
    />
  ) : null
}

const _useCommunityAreas = ({
  selectedArea: initialSelectedArea,
  onChangeCommunityArea
}) => {
  const [selectedAreaId, setSelectedAreaId] = useState(initialSelectedArea)
  const { t, tc } = useTranslate()
  const { cohortId } = useCommunityContext()
  const cohortQuery = useGroupQuery({
    variables: {
      id: cohortId
    },
    skip: !cohortId
  })
  const cohort = get(cohortQuery, "data.group")

  const areaViewPerms = {
    // no CFP mapping for COMMUNITY_AREA_DISCUSSION for now
    [COMMUNITY_AREA_DISCUSSION]: true,
    [COMMUNITY_AREA_MEMBERS]: useCommunityAreaFeaturePermission({
      featureKey: COMMUNITY_FEATURE_VISIBILITY_MAP[COMMUNITY_AREA_MEMBERS]
    }).hasPermission,
    [COMMUNITY_AREA_ACTIVITY]: useCommunityAreaFeaturePermission({
      featureKey: COMMUNITY_FEATURE_VISIBILITY_MAP[COMMUNITY_AREA_ACTIVITY]
    }).hasPermission
  }

  const areas = useMemo(() => {
    const baseAreas = [
      {
        id: COMMUNITY_AREA_DISCUSSION,
        icon: "chat-bubble",
        label: tc("discussion.label"),
        renderContent: (props) => <CommunityDiscussion {...props} />,
        // renderControls: RenderInviteControl,
        renderBlankSlate: (props) =>
          cohort ? (
            <BlankSlate
              {...props}
              heading={t("No discussions so far.")}
              body={t(
                "You'll see all discussion posts in the {{ cohort }} here.",
                {
                  cohort: cohort.name
                }
              )}
            />
          ) : (
            <BlankSlate
              {...props}
              heading={t("Explore discussion all in one place.")}
              body={t(
                "You can explore all the discussions in a cohort in the discussion area. Switch to a cohort (or create a new one) to get started."
              )}
            />
          ),
        canView: areaViewPerms[COMMUNITY_AREA_DISCUSSION]
      },
      {
        id: COMMUNITY_AREA_MEMBERS,
        icon: "avatar-stack",
        label: tc("mentor_group.member.5.label_plural"),
        renderContent: (props) => <CommunityMembers {...props} />,
        // renderControls: RenderInviteControl,
        renderBlankSlate: (props) =>
          cohort ? (
            <BlankSlate
              {...props}
              heading={t("No members so far.")}
              body={t(
                "You'll see a profile of everyone in {{ cohort }} here. ",
                {
                  cohort: cohort.name
                }
              )}
            />
          ) : (
            <BlankSlate
              {...props}
              heading={t("Get to know cohort members.")}
              body={t(
                "You can see each member's profile in the members area. Switch to a cohort (or create a new one) to get started."
              )}
            />
          ),
        canView: areaViewPerms[COMMUNITY_AREA_MEMBERS]
      },
      {
        id: COMMUNITY_AREA_ACTIVITY,
        icon: "activity-feed",
        label: t("Timeline"),
        renderContent: () => <CommunityActivity />,
        // renderControls: RenderInviteControl,
        renderBlankSlate: (props) =>
          cohort ? (
            <BlankSlate
              {...props}
              heading={t("No activity so far.")}
              body={t(
                "You'll see a timeline of progress and discussion-related activity in {{ cohort }} here.",
                {
                  cohort: cohort.name
                }
              )}
            />
          ) : (
            <BlankSlate
              {...props}
              heading={t("Review recent progress and discussion.")}
              body={t(
                "You can see each day's progress and discussion activity in the timeline. Switch to a cohort (or create a new one) to get started."
              )}
            />
          ),
        canView: areaViewPerms[COMMUNITY_AREA_ACTIVITY]
      }
    ]

    // allowing for canView to equal null while permissions are loading
    return baseAreas.filter((area) => area.canView !== false)
  }, [cohortId, cohort, ...Object.values(areaViewPerms)])

  const selectedArea = useMemo(
    () =>
      // ensuring at least the first area is selected in the case where
      // the selectedAreaId does not match any currenty available areas,
      // likely due to the initially selected area not actually being
      // available to the user
      areas.find((area) => area.id === selectedAreaId) || areas[0],
    [selectedAreaId, areas]
  )
  const selectedAreaIndex = useMemo(
    () =>
      // ensuring a minimum index of 0, helpful for avoiding an index
      // of -1 during the brief delay from when areas change and the selectedArea
      // is updated, resulting in the SwitchButton selecting the corresponding selectedAreaId
      Math.max(
        areas.findIndex((area) => area.id === selectedAreaId),
        0
      ),
    [selectedAreaId, areas]
  )

  const renderContent = useCallback(
    (props) => {
      if (!cohortId) {
        return selectedArea.renderBlankSlate(props)
      }
      if (
        selectedArea.canView &&
        typeof selectedArea.renderContent === "function"
      ) {
        return selectedArea.renderContent({ t, tc, ...props })
      }
      return null
    },
    [selectedArea]
  )

  const renderControls = useCallback(
    (props) => {
      if (!cohortId) {
        return null
      }
      if (
        selectedArea.canView &&
        typeof selectedArea.renderControls === "function"
      ) {
        return selectedArea.renderControls({ t, tc, cohort, ...props })
      }
      return null
    },
    [selectedArea]
  )

  // whenever the selectedArea changes, send that change upstream (used by route handler)
  useEffect(() => {
    if (typeof onChangeCommunityArea === "function")
      onChangeCommunityArea(selectedAreaId)
  }, [selectedAreaId])

  const selectArea = (id) => setSelectedAreaId(id)

  return {
    areas,
    selectArea,
    selectedAreaIndex,
    selectedArea,
    renderContent,
    renderControls
  }
}

const useCommunityAreas = () => useContext(CommunityAreaContext)

export const CommunityAreaContextProvider = (props) => {
  const value = _useCommunityAreas(props)
  return (
    <CommunityAreaContext.Provider value={value}>
      {props.children}
    </CommunityAreaContext.Provider>
  )
}

export default useCommunityAreas
